import { Flex, Table, TableBody, TableCell, TableHead, TableRow } from '@aws-amplify/ui-react';
import { ReactNode } from 'react';
import { FaCheck, FaTimes } from 'react-icons/fa';
import GeneralUtils from '../utils/GeneralUtils';
import { CopyToClipboard } from './CopyToClipboard';
import NothingHere from './NothingHere';
import UniversalTag from './UniversalTag';

export interface TeamMemberData {
  userId: string;
  username: string;
  email: string;
  name: string;
  userStatus?: string;
  enabled: boolean;
  userCreateDate?: string;
  customProfile?: string;
  permissions?: string[];
}

const PERMISSION_LABELS: Record<string, string> = {
  'crm-connection:manage': 'Manage CRM Connection',
  'crm-field-mappings:edit': 'Edit CRM Field Mappings',
  'export-institutions-crm:execute': 'Export Institutions to CRM',
  'export-institutions-file:execute': 'Export Institutions to File',
  'export-people-crm:execute': 'Export People to CRM',
  'export-people-file:execute': 'Export People to File',
};

const ALL_PERMISSIONS = Object.keys(PERMISSION_LABELS);

function permissionColumnLabel(perm: string): string {
  return PERMISSION_LABELS[perm] || perm;
}

interface TeamMembersTableProps {
  members: TeamMemberData[];
  /** Current user's email - used to show "ME" tag and prevent self-actions */
  currentUserEmail?: string;
  /** Show permission columns with checkmarks/X */
  showPermissions?: boolean;
  /** Show copy-user-ID control to the left of the name (e.g. boss admin) */
  showUserId?: boolean;
  /** Render custom action button(s) for each member */
  renderActions?: (member: TeamMemberData) => ReactNode;
  /** Whether actions are currently disabled (e.g., during loading) */
  actionsDisabled?: boolean;
  /** Empty state text */
  emptyText?: string;
}

export function TeamMembersTable({
  members,
  currentUserEmail,
  showPermissions = false,
  showUserId = false,
  renderActions,
  actionsDisabled = false,
  emptyText = 'No team members found',
}: TeamMembersTableProps) {
  if (members.length === 0) {
    return <NothingHere text={emptyText} />;
  }

  return (
    <Table className="team-members-table" size="small">
      <TableHead>
        <TableRow>
          <TableCell as="th">Name</TableCell>
          <TableCell as="th">Role</TableCell>
          <TableCell as="th">Status</TableCell>
          {showPermissions && ALL_PERMISSIONS.map(perm => (
            <TableCell
              as="th"
              key={perm}
              style={{ textAlign: 'center', whiteSpace: 'nowrap', fontSize: 12, width: 150 }}
            >
              {permissionColumnLabel(perm)}
            </TableCell>
          ))}
          {renderActions && <TableCell as="th"></TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {members.map((member) => {
          const isCurrentUser = currentUserEmail && member.email === currentUserEmail;

          return (
            <TableRow key={member.userId || member.email}>
              <TableCell>
                <Flex alignItems="center" gap={6}>
                  {showUserId && member.userId && (
                    <CopyToClipboard value={member.userId} title="Copy user ID" />
                  )}
                  <span
                    data-tooltip-id="main-tooltip"
                    data-tooltip-content={`${member.email}${member.userCreateDate ? ` • Joined ${GeneralUtils.formatDate(member.userCreateDate)}` : ''}`}
                  >
                    {member.name || member.email}
                  </span>
                  {isCurrentUser && <UniversalTag category="ME" />}
                </Flex>
              </TableCell>
              <TableCell>
                <UniversalTag category={member.customProfile || 'MEMBER'} />
              </TableCell>
              <TableCell>
                <UniversalTag category={member.enabled ? 'ENABLED' : 'DISABLED'} />
              </TableCell>
              {showPermissions && ALL_PERMISSIONS.map(perm => {
                const hasPermission = member.permissions?.includes(perm);
                const userName = member.name || member.email;
                const tooltipText = hasPermission
                  ? `${userName} is allowed to ${PERMISSION_LABELS[perm]}`
                  : `${userName} is not allowed to ${PERMISSION_LABELS[perm]}`;
                return (
                  <TableCell key={perm} style={{ textAlign: 'center' }}>
                    <span data-tooltip-id="main-tooltip" data-tooltip-content={tooltipText}>
                      {hasPermission
                        ? <FaCheck style={{ color: 'var(--amplify-colors-green-60)' }} />
                        : <FaTimes style={{ color: 'var(--amplify-colors-neutral-40)' }} />}
                    </span>
                  </TableCell>
                );
              })}
              {renderActions && (
                <TableCell style={{ textAlign: 'right' }}>
                  {renderActions(member)}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
